// Unified data layer for Incident Reports.
// Supabase when configured; localStorage demo store otherwise.

import { supabase, isSupabaseConfigured } from "./supabase";
import {
  demoAcknowledgeIncident,
  demoCreateIncident,
  demoGetIncident,
  demoListIncidents,
  demoResolveIncident,
} from "./incidentsDemo";
import { listChildrenForParent } from "./dailyReports";

const INCIDENT_SELECT = `
  id, organization_id, child_id, classroom_id,
  incident_type, severity, occurred_at, location,
  description, action_taken,
  parent_notified_at, parent_notification_method,
  status, reported_by,
  acknowledged_by, acknowledged_at,
  resolved_by, resolved_at, resolution_note,
  created_at, updated_at,
  child:children(id, first_name, last_name, family_id),
  classroom:classrooms(id, name),
  reporter:profiles!incidents_reported_by_fkey(id, full_name, email),
  acknowledger:profiles!incidents_acknowledged_by_fkey(id, full_name, email),
  resolver:profiles!incidents_resolved_by_fkey(id, full_name, email)
`;

export async function listIncidents({ role, profileId, filters = {} }) {
  if (!isSupabaseConfigured) {
    let childIds;
    if (role === "parent") {
      const kids = await listChildrenForParent(profileId);
      childIds = kids.map((c) => c.id);
    }
    return demoListIncidents({ role, profileId, childIds, filters });
  }
  let query = supabase
    .from("incidents")
    .select(INCIDENT_SELECT)
    .order("occurred_at", { ascending: false });

  if (filters.classroomId) query = query.eq("classroom_id", filters.classroomId);
  if (filters.childId)     query = query.eq("child_id", filters.childId);
  if (filters.severity)    query = query.eq("severity", filters.severity);
  if (filters.status)      query = query.eq("status", filters.status);
  if (filters.dateFrom)    query = query.gte("occurred_at", `${filters.dateFrom}T00:00:00`);
  if (filters.dateTo)      query = query.lte("occurred_at", `${filters.dateTo}T23:59:59`);

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

export async function getIncident(id) {
  if (!isSupabaseConfigured) return demoGetIncident(id);
  const { data, error } = await supabase
    .from("incidents")
    .select(INCIDENT_SELECT)
    .eq("id", id)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function createIncident(payload) {
  if (!isSupabaseConfigured) return demoCreateIncident(payload);
  const insertRow = {
    organization_id: payload.organizationId,
    child_id: payload.childId,
    classroom_id: payload.classroomId,
    incident_type: payload.incidentType,
    severity: payload.severity,
    occurred_at: payload.occurredAt,
    location: payload.location || null,
    description: payload.description,
    action_taken: payload.actionTaken,
    parent_notified_at: payload.parentNotifiedAt || null,
    parent_notification_method: payload.parentNotificationMethod || null,
    reported_by: payload.reportedBy,
  };
  const { data, error } = await supabase
    .from("incidents")
    .insert(insertRow)
    .select(INCIDENT_SELECT)
    .single();
  if (error) throw error;
  return data;
}

export async function acknowledgeIncident({ incidentId, profileId }) {
  if (!isSupabaseConfigured) {
    return demoAcknowledgeIncident({ incidentId, profileId });
  }
  // Parents go through the SECURITY-DEFINER RPC so they can touch only ack columns.
  const { data, error } = await supabase.rpc("rpc_acknowledge_incident", {
    p_incident_id: incidentId,
  });
  if (error) throw error;
  return data;
}

export async function resolveIncident({ incidentId, profileId, note }) {
  if (!isSupabaseConfigured) {
    return demoResolveIncident({ incidentId, profileId, note });
  }
  const { data, error } = await supabase
    .from("incidents")
    .update({
      resolved_by: profileId,
      resolved_at: new Date().toISOString(),
      resolution_note: note || null,
      status: "resolved",
    })
    .eq("id", incidentId)
    .select(INCIDENT_SELECT)
    .single();
  if (error) throw error;
  return data;
}

/**
 * Admin-only structured edit. Goes through the SECURITY-DEFINER RPC which
 * applies strict edit rules:
 *   • before parent acknowledgement → all fields editable
 *   • after acknowledgement         → only resolution_note + status to 'resolved'
 *   • once resolved                 → read-only (rejected with P0001)
 * Pass only the fields you want to change in `patch`.
 */
export async function adminUpdateIncident({ incidentId, patch }) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  const { error } = await supabase.rpc("rpc_admin_update_incident", {
    p_incident_id: incidentId,
    p_patch: patch || {},
  });
  if (error) throw error;
  // The RPC returns the partial row; refetch with the embeds we need.
  return getIncident(incidentId);
}

/** Calm timeline of every change applied to an incident. */
export async function listIncidentAuditEvents(incidentId) {
  if (!isSupabaseConfigured) return [];
  const { data, error } = await supabase
    .from("incident_audit_events")
    .select(`
      id, action, changes, note, created_at, actor_profile_id,
      actor:profiles!incident_audit_events_actor_profile_id_fkey(id, full_name, email)
    `)
    .eq("incident_id", incidentId)
    .order("created_at", { ascending: true });
  if (error) {
    // Older databases may not have the audit table yet — fail soft so the
    // detail page keeps working.
    if (error.code === "42P01") return [];
    throw error;
  }
  return data || [];
}
